/*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/

import { Journey } from '@/data/schema/journey'
import JourneyService from '@/services/journey'
import useToasterStore from '@/stores/toaster'
import { useRoute } from 'vue-router'
import { defineStore } from 'pinia'
import { isset } from '@/utils/string'
import { deliveryQuestions, collectionQuestions } from '@/utils/journeys'
import type { SurveyResponse } from '@/data/types/survey-response'
import type { FileResponse } from '@/data/types/file-response'
import router from '@/router'
import useAuthStore from './auth'

export default defineStore('driver-app', {
	state: () => ({
		isLoading: true,
		resumed: false,
		journey: null as Journey | null,
		photosSaved: false,
		signatureSaved: false,
		surveySaved: false
	}),

	getters: {
		showActions(state) {
			const auth = useAuthStore()
			return auth.vehicleLocked || state.journey?.complete
		},

		storedJourney() {
			if (!localStorage.getItem('journey')) {
				return null
			}

			return JSON.parse(localStorage.getItem('journey')!)
		},

		survey(state) {
			if (!state.journey) {
				return []
			}
			return state.journey!.survey
		},

		photos(state) {
			if (!state.journey) {
				return []
			}
			return state.journey!.photos
		},

		signature(state) {
			return state.journey?.signature
		},

		surveyQuestions(state) {
			return state.journey?.type == 'DEL' ? deliveryQuestions : collectionQuestions
		},

		filteredSurvey(): SurveyResponse[] {
			return this.survey!.filter((qa) => qa.answer.trim() !== '')
		},

		photosSkipped(state) {
			return state.photosSaved && state.journey?.photo_skip_reason ? true : false
		},

		unsavedPhotos(): File[] {
			if (!this.photos || this.photos!.length == 0) {
				return []
			}
			return this.photos.filter((file) => !('path' in file)) as File[]
		},

		preparedPhotos(): File[] {
			return this.unsavedPhotos.map((file) => {
				return new File([file], file.name, { type: file.type })
			})
		},

		step1Complete(state) {
			if (state.journey!.complete) {
				return true
			}
			return state.journey?.site_depart_at != null
		},

		step2Complete(state) {
			if (state.journey!.complete) {
				return true
			}

			const skipReason = state.journey?.photo_skip_reason
			const hasPhotos = state.journey?.photos && state.journey?.photos.length > 0
			const skipReasonOther = state.journey?.photo_skip_reason_other

			return (
				hasPhotos ||
				(skipReason && skipReason !== 'Other') ||
				(skipReason === 'Other' && skipReasonOther && skipReasonOther.trim() !== '')
			)
		},

		step3Complete(state) {
			if (state.journey!.complete) {
				return true
			}
			return state.journey?.signature != null && state.journey?.signature_name != ''
		},

		step4Complete(state) {
			if (state.journey!.complete) {
				return true
			}

			if (isset(state.journey!.survey?.[0]?.answer)) {
				if (
					state.journey!.survey?.[0]?.answer == 'Other' &&
					!state.journey!.survey_skip_reason
				) {
					return false
				}

				return true
			}

			return (
				isset(state.journey!.survey?.[1]?.answer) &&
				isset(state.journey!.survey?.[2]?.answer) &&
				isset(state.journey!.survey?.[3]?.answer) &&
				isset(state.journey!.survey?.[5]?.answer)
			)
		},

		stepsComplete() {
			if (this.journey!.complete) {
				return true
			}

			if (
				this.step1Complete &&
				this.step2Complete &&
				this.step3Complete &&
				this.step4Complete
			) {
				return true
			}

			return false
		}
	},

	actions: {
		loading(value: boolean) {
			this.isLoading = value
		},

		setJourney(journey: Journey) {
			this.journey = journey

			if (journey.files && journey.files.length > 0) {
				this.journey!.photos = this.journey!.files.filter(
					(file: FileResponse) => file.type === 'photo'
				)
				this.journey!.signature = this.journey!.files.filter(
					(file: FileResponse) => file.type === 'signature'
				)?.[0]?.path
			}

			this.setSavedStates()
		},

		setSavedStates(init = false) {
			if (init) {
				this.surveySaved = false
			} else {
				this.photosSaved = !!(
					(this.photos && this.photos.length > 0) ||
					this.journey!.photo_skip_reason
				)
				this.signatureSaved = this.signature != null
			}
		},

		arrived() {
			this.journey!.site_arrive_at = new Date()
		},

		start_loading() {
			this.journey!.load_start_at = new Date()
		},

		end_loading() {
			this.journey!.site_depart_at = new Date()
		},

		/**
		 * Ran onMounted at each step
		 * Get a fresh journey or save progress
		 */
		initialise(save = true) {
			const route = useRoute()
			const id = String(route.params.id)

			// If no active journey or route param ID doesnt match
			if (!this.journey || id != this.journey.id) {
				this.setSavedStates(true)

				return this.getJourney()
			} else if (save) {
				return this.saveJourney()
			}

			return Promise.resolve(this.journey)
		},

		/**
		 * Get journey data from API
		 */
		getJourney(): Promise<Journey | string> {
			const api = new JourneyService()
			const toaster = useToasterStore()
			const route = useRoute()
			const id = String(route.params.id)

			return new Promise((resolve) => {
				// Offline
				if (!navigator.onLine) {
					// If offline, just use journey state
					resolve(this.journey!)
				} else {
					// If localstorage journey ID matches route param ID, resume
					if (!this.journey && this.storedJourney && id == this.storedJourney.id) {
						this.resumeJourney()
						return resolve(this.journey!)
					}

					// otherwise, reset and reload journey from API
					this.removeJourney()
					this.loading(true)
					api.get(id, { withItems: true, withFiles: true })
						.then((res) => {
							this.setJourney(res.data.data)
							this.setSavedStates()

							// Get survey
							if (!this.survey || this.survey.length == 0) {
								this.getSurvey().then(() => {
									resolve(this.journey!)
								})
							} else {
								resolve(this.journey!)
							}
						})
						.catch((error) => {
							if (error.response?.status === 404) {
								router.push({
									name: 'not-found',
									params: {
										notFound: route.path.substring(1).split('/')
									}
								})
							}

							console.error(error)
							toaster.error({
								text: 'Error occurred getting journey data'
							})
							this.loading(false)
							resolve(error)
						})
				}
			})
		},

		/**
		 * Save journey progress server-side
		 */
		saveJourney(complete = false): Promise<Journey | string> {
			const api = new JourneyService()
			const toaster = useToasterStore()
			// const router = useRouter()

			// Filter out keys we dont want to send
			const filteredJourney = (({ driver_status, signature, survey, photos, ...rest }) =>
				rest)(this.journey as Journey)

			toaster.working({
				text: 'Saving progress...'
			})

			return new Promise((resolve) => {
				// Offline
				if (!navigator.onLine) {
					this.storeJourney()
					resolve('No network connection')
				} else {
					api.updateJourney(this.journey!.id, { ...filteredJourney })
						.then(() => {
							toaster.clear()

							if (this.resumed) {
								if (this.unsavedPhotos.length > 0) {
									this.savePhotos()
								}

								if (!this.signatureSaved && this.signature) {
									this.saveSignature()
								}

								if (!this.surveySaved && this.step4Complete) {
									this.saveSurvey()
								}
							}

							resolve(this.journey!)

							if (!complete) {
								toaster.success({ text: 'Progress saved successfully!' })
							} else {
								api.completeJourney(this.journey!.id)
									.then(() => {
										toaster.success({
											text: 'Journey complete! Redirecting...'
										})
										router.push({ name: 'driver-app-dashboard' })
										this.removeJourney()
										this.journey = null
									})
									.catch((error) => {
										toaster.error({
											text: 'Failed to confirm journey. Please try again'
										})
										console.error(error)
									})
							}

							// clear any local storage
							this.removeJourney()
						})
						.catch((error) => {
							// If 503, store journey in localstorage
							if (error.response.status === 503) {
								this.storeJourney()
							} else {
								toaster.clear()
								console.error(error)
								toaster.error({
									text: 'Error occurred saving journey progress'
								})
							}

							this.loading(false)
							resolve(error)
						})
				}
			})
		},

		/**
		 * Resume a journey from localstorage
		 */
		resumeJourney() {
			this.journey = this.storedJourney
			this.resumed = true
		},

		/**
		 * Save journey progress client-side
		 */
		storeJourney() {
			const toaster = useToasterStore()
			toaster.clear()
			toaster.warning({ text: 'Service unavailable. Progress saved to device.' })
			localStorage.setItem('journey', JSON.stringify(this.journey))
			this.resumed = false
		},

		/**
		 * Remove journey progress from client-side storage
		 */
		removeJourney() {
			localStorage.removeItem('journey')
			this.resumed = false
		},

		/**
		 * User has requested to force reload journey
		 */
		reloadJourney() {
			this.removeJourney()
			this.getJourney()
		},

		savePhotos() {
			const api = new JourneyService()
			const toaster = useToasterStore()

			if (this.journey!.photo_skip_reason) {
				if (
					this.journey!.photo_skip_reason === 'Other' &&
					this.journey!.photo_skip_reason_other
				) {
					this.journey!.photo_skip_reason = `Other - ${this.journey!.photo_skip_reason_other}`
				}

				this.setSavedStates()
				return Promise.resolve('Photo skipped')
			} else {
				// Offline
				if (!navigator.onLine) {
					this.storeJourney()
				} else {
					toaster.working({
						text: 'Saving photos...'
					})

					return api
						.savePhotos(this.journey!.id, this.preparedPhotos)
						.then(() => {
							toaster.clear()

							toaster.success({
								text: 'Photos saved successfully!'
							})

							this.getFiles()
						})
						.catch((error) => {
							toaster.clear()
							console.error(error)
							toaster.error({
								text: 'Error occurred saving the photos'
							})

							// re-throw for child promises
							throw error
						})
				}
			}
		},

		saveSignature() {
			const api = new JourneyService()
			const toaster = useToasterStore()

			// Offline
			if (!navigator.onLine) {
				this.storeJourney()
			} else {
				this.loading(true)

				toaster.working({
					text: 'Saving signature...'
				})

				return api
					.saveSignature(this.journey!.id, this.signature as string)
					.then(() => {
						toaster.clear()

						toaster.success({
							text: 'Signature saved successfully!'
						})

						this.getFiles()
						this.loading(false)
					})
					.catch((error) => {
						toaster.clear()
						console.error(error)
						toaster.error({
							text: 'Error occurred saving the signature'
						})
						this.loading(false)

						// re-throw for child promises
						throw error
					})
			}
		},

		getSurvey() {
			const api = new JourneyService()
			const toaster = useToasterStore()

			return api
				.getSurvey(this.journey!.id)
				.then((res) => {
					if (res.data.data.length > 0) {
						this.journey!.survey = res.data.data
						this.surveySaved = true
					} else {
						this.resetSurvey()
					}

					this.loading(false)
				})
				.catch((error) => {
					console.error(error)
					toaster.error({
						text: 'Error occurred getting survey data'
					})
					this.loading(false)
				})
		},

		resetSurvey() {
			const questions: SurveyResponse[] = []
			this.journey!.survey = []

			this.surveyQuestions.forEach((question: string) => {
				questions.push({
					question: question,
					answer: ''
				})
			})

			this.journey!.survey = questions
		},

		saveSurvey() {
			const api = new JourneyService()
			const toaster = useToasterStore()

			// Offline
			if (!navigator.onLine) {
				this.storeJourney()
			} else {
				toaster.working({
					text: 'Saving survey...'
				})

				// Concatenate "Other" survey skip values
				if (this.survey[0]?.answer == 'Other') {
					this.journey!.survey[0].answer = `Other - ${this.journey?.survey_skip_reason}`
				}

				return api
					.saveSurvey(this.journey!.id, this.filteredSurvey)
					.then(() => {
						this.surveySaved = true
						toaster.clear()

						toaster.success({
							text: 'Survey saved successfully!'
						})

						this.loading(false)
					})
					.catch((error) => {
						toaster.clear()
						console.error(error)
						toaster.error({
							text: 'Error occurred saving the survey'
						})
						this.loading(false)

						// re-throw for child promises
						throw error
					})
			}
		},

		getFiles() {
			const api = new JourneyService()
			const toaster = useToasterStore()

			return api
				.getFiles(this.journey!.id)
				.then((res) => {
					this.journey!.photos = res.data.data.filter(
						(file: FileResponse) => file.type === 'photo'
					)
					this.journey!.signature = res.data.data.filter(
						(file: FileResponse) => file.type === 'signature'
					)[0].path
				})
				.catch((error) => {
					console.error(error)
					toaster.error({
						text: 'Error occurred getting file data'
					})
				})
				.finally(() => {
					this.setSavedStates()
				})
		}
	}
})
